import { ConvergingTask } from '@/platform/ConvergingTask';
import { EmbeddedStatePropagatedEvent } from '@/url/events/EmbeddedStatePropagatedEvent';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { ref } from 'vue';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { Url } from '@/url/Url';
import { injectable } from 'inversify';
import { PortalType } from '@studyportals/domain-client';
import { EmbeddedStateFactory } from '@/url/EmbeddedStateFactory';
import { Degree } from '@/models/Degree';
import { FilterSelectionState } from '@/filters/selection/FilterSelectionState';
import { FilterKey } from '@studyportals/search-filters';
import { FilterSelection } from '@/filters/selection/FilterSelection';

@injectable()
export class CanonicalUrl extends ConvergingTask<string, Url> implements ISubscriber<EmbeddedStatePropagatedEvent> {
	public readonly canonical = ref('');

	private readonly eventAggregationService: EventAggregationService;
	private readonly url: Url;
	private readonly embeddedState: EmbeddedStateFactory;
	private readonly filterSelectionState: FilterSelectionState;

	public constructor(
		eventAggregationService: EventAggregationService,
		url: Url,
		embeddedState: EmbeddedStateFactory,
		filterSelection: FilterSelection
	) {
		super();

		this.eventAggregationService = eventAggregationService;
		this.url = url;
		this.embeddedState = embeddedState;
		this.filterSelectionState = filterSelection.state;

		void this.refresh(url);

		this.eventAggregationService.subscribeTo(EmbeddedStatePropagatedEvent.EventType, this);
	}

	private get mastersPortalUrl(): string | undefined {
		if (!window.PortalMap) return undefined;

		return window.PortalMap[PortalType.MASTER];
	}

	public notify(event: EmbeddedStatePropagatedEvent): void {
		if (EmbeddedStatePropagatedEvent.is(event)) {
			void this.refresh(this.url);
		}
	}

	public isSelfCanonical(): boolean {
		const url = new URL(window.location.href);
		url.searchParams.delete('bot');
		return this.canonical.value === url.toString();
	}

	protected override async run(url: Url): Promise<string> {
		const baseUrl = this.getBaseUrl();

		const path = await this.constructPath(url);

		return `${baseUrl}/search${path}`;
	}

	protected override commit(result: string): void {
		this.canonical.value = result;
	}

	protected override queued(): boolean {
		return false;
	}

	private async refresh(url: Url): Promise<void> {
		await this.execute(url);
	}

	private getBaseUrl(): string {
		return this.mastersPortalUrl ?? this.url.currentOrigin();
	}

	private async constructPath(url: Url): Promise<string> {
		const selectionForUniversity = this.filterSelectionState.reactive.getFor(FilterKey.ORGANISATIONS);
		const isUniversitySelected = selectionForUniversity.length > 0;

		let state = isUniversitySelected ? this.embeddedState.default() : this.embeddedState.fromCommitted();

		if (this.mastersPortalUrl) {
			state = state.toMutable().withDegree(Degree.master);
		}

		return await url.pathFor(state);
	}
}
